import React, { useEffect, useRef, useState } from "react";
import { ArrowClockwise, Clipboard, Printer } from "react-bootstrap-icons";
import {
  buildClipboardPayload,
  writeClipboard,
} from "../EOYReportAssistant/ReportCard";
import { printReportNodes } from "../EOYReportAssistant/printReports";
import ScoresTable from "../EOYReportAssistant/ScoresTable";
import ScoresVisibilityToggle from "../EOYReportAssistant/ScoresVisibilityToggle";
import PrintHeader from "../PrintHeader";
import { reportHasContent } from "./useEnhancedReportWorkflow";
import "../PrintStudentResults.css";
import "../EOYReportAssistant/EOYReportAssistant.css";

const SCREENING_POINT_LABELS = {
  baseline: "Baseline",
  midpoint: "Midpoint",
  end_of_year: "End of Year",
};

function clearTimer(timerRef) {
  if (!timerRef.current) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

/**
 * Screening Point report card — read only (no save endpoint exists for this
 * report type). Reuses ScoresTable directly since the response's `scores`
 * field is the same {name, date, tasks} shape ScoresTable already renders
 * for EOY.
 */
function ScreeningPointReportCard({
  report,
  className,
  academicYear,
  onReportNodeReady,
  onRegenerateReport,
  regenerating,
}) {
  const [showScores, setShowScores] = useState(true);
  const [copyStatus, setCopyStatus] = useState("");
  const reportShellRef = useRef(null);
  const activePrintCleanupRef = useRef(null);
  const copyStatusTimer = useRef(null);

  const studentId = report?.student_id;

  useEffect(() => {
    return () => {
      clearTimer(copyStatusTimer);
      activePrintCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!report?.success || !studentId || !onReportNodeReady) return undefined;
    onReportNodeReady(studentId, reportShellRef.current);
    return () => onReportNodeReady(studentId, null);
  }, [onReportNodeReady, studentId, report?.success]);

  if (!report) return null;

  const displayId = report.display_name || report.student_id;
  const isRegenerationAvailable = report.can_regenerate === true;

  if (!report.success) {
    return (
      <div className="eoy-report-card eoy-report-card-error">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <span className="eoy-report-badge eoy-report-badge-error">
            {isRegenerationAvailable ? "Update available" : "Failed"}
          </span>
        </div>
        <p className="eoy-report-text">
          {report.error?.message || "Report could not be generated."}
        </p>
        {onRegenerateReport && studentId && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  const screeningPointLabel =
    SCREENING_POINT_LABELS[report.screening_point] || report.screening_point;

  // A successful response can still carry no usable narrative — either an
  // explicit placeholder (no completed screener for this point) or, more
  // rarely, an empty cached record. Both need to say so rather than
  // rendering scores/headers around a blank body.
  if (report.placeholder || !reportHasContent(report)) {
    return (
      <div className="eoy-report-card eoy-report-card-placeholder">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <small>{screeningPointLabel} screening point</small>
        </div>
        <p className="eoy-report-text eoy-muted">
          {report.report ||
            `No completed ${screeningPointLabel} screening information is available for ${displayId}${
              report.academic_year || academicYear ? ` in ${report.academic_year || academicYear}` : ""
            }.`}
        </p>
        {onRegenerateReport && studentId && !report.placeholder && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  const screeners = report.scores || [];

  const handlePrintReport = () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;
    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes([reportNode]);
  };

  const handleCopyReport = async () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;

    try {
      const copied = await writeClipboard(buildClipboardPayload(reportNode));
      setCopyStatus(copied ? "Copied" : "Copy failed");
    } catch (err) {
      console.warn("Could not copy Screening Point report:", err);
      setCopyStatus("Copy failed");
    }
    clearTimer(copyStatusTimer);
    copyStatusTimer.current = window.setTimeout(() => {
      setCopyStatus("");
      copyStatusTimer.current = null;
    }, 1800);
  };

  return (
    <div className="eoy-report-print-shell" ref={reportShellRef}>
      <div className="eoy-report-card">
        <PrintHeader
          className={className}
          academicYear={report.academic_year || academicYear}
          assessmentName={report.assessment_name || screeningPointLabel}
        />
        <div className="eoy-report-toolbar no-print">
          <span
            className={`eoy-copy-status ${copyStatus ? "" : "eoy-copy-status-hidden"}`}
            aria-live="polite"
          >
            {copyStatus || "Copied"}
          </span>
          {screeners.length > 0 && (
            <ScoresVisibilityToggle
              checked={showScores}
              tooltip="Show or hide assessment results in this report"
              onChange={setShowScores}
            />
          )}
          <button
            aria-label="Copy report to clipboard"
            className="eoy-toolbar-icon-btn"
            onClick={handleCopyReport}
            data-tooltip="Copy report to clipboard"
            type="button"
          >
            <Clipboard aria-hidden="true" size={16} />
          </button>
          <button
            aria-label="Print this report"
            className="eoy-toolbar-icon-btn"
            onClick={handlePrintReport}
            data-tooltip="Print this report"
            type="button"
          >
            <Printer aria-hidden="true" size={16} />
          </button>
        </div>

        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <small>{screeningPointLabel} screening point</small>
        </div>

        <ScoresTable screeners={screeners} showScores={showScores} />

        <div className="eoy-parent-report-section">
          <p className="eoy-report-text no-print">{report.report}</p>
          <div className="eoy-report-print-summary" aria-hidden="true">
            {report.report}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScreeningPointReportCard;
